import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkCjkStrong from '../utils/remarkCjkStrong';
import { COLORS, RADIUS } from '../utils/uiStyles';
import { safeUrl } from '../utils/safeUrl';

const baseText = {
  color: COLORS.text,
  fontSize: '14px',
  lineHeight: 1.7,
  wordBreak: 'break-word',
};

const tableCell = {
  border: `1px solid ${COLORS.borderSoft}`,
  padding: '6px 8px',
  textAlign: 'left',
  verticalAlign: 'top',
};

const getTextStyle = ({ compact, muted }) => ({
  ...baseText,
  color: muted ? COLORS.textSub : (compact ? 'inherit' : COLORS.text),
  ...(compact ? { fontSize: 'inherit', lineHeight: 'inherit' } : {}),
});

const createComponents = ({ compact, muted }) => {
  const text = getTextStyle({ compact, muted });
  const strongColor = muted ? COLORS.textSub : (compact ? 'inherit' : COLORS.text);
  const subColor = muted ? COLORS.textMuted : COLORS.textSub;
  const headingSize = (size, compactSize) => (compact ? compactSize : size);

  return {
    h1: ({ children }) => <h1 style={{ ...text, fontSize: headingSize('18px', '1.1em'), fontWeight: 660, margin: compact ? '0 0 6px' : '0 0 10px' }}>{children}</h1>,
    h2: ({ children }) => <h2 style={{ ...text, fontSize: headingSize('16px', '1.05em'), fontWeight: 620, margin: compact ? '8px 0 5px' : '12px 0 8px' }}>{children}</h2>,
    h3: ({ children }) => <h3 style={{ ...text, fontSize: headingSize('15px', '1em'), fontWeight: 620, margin: compact ? '6px 0 4px' : '10px 0 6px' }}>{children}</h3>,
    p: ({ children }) => <p style={{ ...text, margin: compact ? '0 0 4px' : '0 0 8px' }}>{children}</p>,
    ul: ({ children }) => <ul style={{ ...text, margin: compact ? '2px 0 6px' : '4px 0 10px', paddingLeft: compact ? '18px' : '20px' }}>{children}</ul>,
    ol: ({ children }) => <ol style={{ ...text, margin: compact ? '2px 0 6px' : '4px 0 10px', paddingLeft: compact ? '18px' : '20px' }}>{children}</ol>,
    li: ({ children }) => <li style={{ margin: compact ? '1px 0' : '2px 0', paddingLeft: '2px' }}>{children}</li>,
    strong: ({ children }) => <strong style={{ color: strongColor, fontWeight: 630 }}>{children}</strong>,
    em: ({ children }) => <em style={{ color: subColor }}>{children}</em>,
    table: ({ children }) => (
      <div style={{ overflowX: 'auto', margin: compact ? '6px 0 8px' : '8px 0 12px' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: '420px', fontSize: compact ? 'inherit' : '13px', lineHeight: compact ? 'inherit' : 1.55 }}>
          {children}
        </table>
      </div>
    ),
    th: ({ children }) => <th style={{ ...tableCell, color: strongColor, fontWeight: 600, background: COLORS.bg }}>{children}</th>,
    td: ({ children }) => <td style={{ ...tableCell, color: muted ? COLORS.textSub : (compact ? 'inherit' : COLORS.textSub) }}>{children}</td>,
    code: ({ children }) => (
      <code style={{ background: COLORS.bg, border: `1px solid ${COLORS.borderSoft}`, borderRadius: RADIUS.sm, padding: '1px 4px', color: strongColor, fontSize: compact ? '0.95em' : '12px' }}>
        {children}
      </code>
    ),
    pre: ({ children }) => (
      <pre style={{ background: COLORS.bg, border: `1px solid ${COLORS.borderSoft}`, borderRadius: RADIUS.md, padding: compact ? '8px 10px' : '10px 12px', overflowX: 'auto', margin: compact ? '6px 0 8px' : '8px 0 12px' }}>
        {children}
      </pre>
    ),
    a: ({ href, children }) => {
      const url = safeUrl(href);
      if (!url) return <span style={{ color: subColor }}>{children}</span>;
      return (
        <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: COLORS.info, textDecoration: 'none', fontWeight: 500 }}>
          {children}
        </a>
      );
    },
  };
};

export default function Markdown({ children, compact = false, muted = false }) {
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm, remarkCjkStrong]} components={createComponents({ compact, muted })}>
      {children || ''}
    </ReactMarkdown>
  );
}
